import type { FileRouteTypes } from "@/routeTree.gen";
import { USER_LOGGED_IN_KEY, currentUserAtom } from "@/store/login";
import {
	rememberTelegramConnectTarget,
	telegramBackUrlAtom,
} from "@/store/telegram";
import { redirect } from "@tanstack/react-router";
import { getDefaultStore } from "jotai";
import { canAccessRoute } from "./role";

function getUser() {
	const store = getDefaultStore();

	if (!store.get(currentUserAtom)) {
		const storedUser = localStorage.getItem(USER_LOGGED_IN_KEY);
		if (storedUser) {
			store.set(currentUserAtom, JSON.parse(storedUser));
		}
	}

	return store.get(currentUserAtom);
}

export function authMiddleware(to: string) {
	const finalUser = getUser();

	if (!finalUser) {
		return redirect({ to: "/login" });
	}

	const allowed = canAccessRoute(finalUser.role, to as FileRouteTypes["to"]);

	if (!allowed) {
		switch (finalUser.role) {
			case "ADMIN":
				return redirect({ to: "/admin/dashboard" });
			case "PO":
				return redirect({ to: "/payment-history" });
			case "MARKETER":
				return redirect({ to: "/login" });
			case "FC_USER":
				return redirect({ to: "/dashboard" });
			default:
				finalUser.role satisfies never;
		}
	}

	return true;
}

/** Sends an already logged in user to their landing page instead of /login. */
export function guestMiddleware() {
	const user = getUser();

	// MARKETER has no app route (login toasts "no access"), so it stays here.
	if (!user || user.role === "MARKETER") {
		return true;
	}

	const telegramTarget = getDefaultStore().get(telegramBackUrlAtom);

	if (telegramTarget) {
		// Only FC_USER is let through by telegramConnectMiddleware; sending any
		// other role there would bounce straight back to /login forever, so they
		// keep the form to sign in as the FC user the bot link belongs to.
		if (user.role !== "FC_USER") {
			return true;
		}
		getDefaultStore().set(telegramBackUrlAtom, null);
		throw redirect({ ...telegramTarget });
	}

	switch (user.role) {
		case "ADMIN":
			throw redirect({ to: "/admin/dashboard" });
		case "PO":
			throw redirect({ to: "/payment-history" });
		case "FC_USER":
			throw redirect({ to: "/dashboard" });
		default:
			user.role satisfies never;
			return true;
	}
}

export function telegramConnectMiddleware(token?: string) {
	const finalUser = getUser();

	if (!finalUser || finalUser.role !== "FC_USER") {
		rememberTelegramConnectTarget(token);
		return redirect({ to: "/login" });
	}

	return true;
}
